/**
 * Usage metering — the counters `usage_counters` holds, the window they are counted in, and
 * the one rule that turns a count and a limit into a meter's state.
 *
 * The 80% threshold is `OVERAGE.alertAtPercentOfLimit` (`plans.ts`), never retyped here.
 */
import { SQL_PATTERNS, posixToJs } from "./api/patterns.js";
import { OVERAGE, PLANS } from "./plans.js";
/** Held set-equal to `0031`'s `usage_counters_metric_check`. `TR:770`. */
export const USAGE_METRICS = ["gtins_active", "resolver_requests", "api_calls"];
/**
 * The metrics the rollup writes. `api_calls` has no writer until Phase 7, and a meter over a
 * counter nothing writes renders a false 0 — plan §0.14.
 */
export const ROLLED_UP_METRICS = [
    "gtins_active",
    "resolver_requests",
];
/** `2026-10` — the calendar month in UTC, `TR:770`'s spelling. */
export function usageWindow(at) {
    const month = String(at.getUTCMonth() + 1).padStart(2, "0");
    return `${String(at.getUTCFullYear())}-${month}`;
}
/** `usage_counters_window_check`'s grammar. */
export const USAGE_WINDOW = posixToJs(SQL_PATTERNS.usageWindow);
/** The window's half-open bounds, `[start, end)`, in UTC. */
export function usageWindowBounds(window) {
    if (!USAGE_WINDOW.test(window)) {
        throw new Error(`${window} is not a usage window (YYYY-MM)`);
    }
    const year = Number(window.slice(0, 4));
    const month = Number(window.slice(5, 7)) - 1;
    return {
        start: new Date(Date.UTC(year, month, 1)),
        end: new Date(Date.UTC(year, month + 1, 1)),
    };
}
/**
 * Where `resolver_requests` starts counting: the window's start, or a plan change inside the
 * window. A plan change resets the fair-use floor — Q-P16, ruled 2026-10-29.
 */
export function usageFloor(window, planChangedAt) {
    const { start, end } = usageWindowBounds(window);
    return planChangedAt !== null &&
        planChangedAt.getTime() > start.getTime() &&
        planChangedAt.getTime() < end.getTime()
        ? planChangedAt
        : start;
}
/** The window before this one. */
export function previousUsageWindow(window) {
    const { start } = usageWindowBounds(window);
    return usageWindow(new Date(start.getTime() - 1));
}
/** What a tier includes of one metric, or `null` where nothing is included to exceed. */
export function usageLimit(tier, metric) {
    switch (metric) {
        case "gtins_active":
            return PLANS[tier].activeGtins;
        case "resolver_requests":
            return PLANS[tier].fairUsePerMonth;
        case "api_calls":
            // C11: the pricing contract defines no API-call ceiling.
            return null;
    }
}
/**
 * At or over 80% is `near-limit`, past the limit is `over-limit` — `components.md` → Usage
 * meter. Integer arithmetic, so 80% exact is near and 79.99% is not.
 */
export function meterState(value, limit) {
    if (limit === null || limit <= 0)
        return "clear";
    if (value > limit)
        return "over-limit";
    if (value * 100 >= limit * OVERAGE.alertAtPercentOfLimit)
        return "near-limit";
    return "clear";
}
//# sourceMappingURL=usage.js.map